// Waits between token attempts. Total ~1.75s before the caller sees a failure.
const TOKEN_RETRY_DELAYS_MS = [250, 500, 1000];

/**
 * Calls `fetchToken` until it returns a token, retrying after each delay while signed in.
 * Returns the last result or rethrows the last error. `onRetried` reports the attempt count
 * whenever more than one attempt was needed.
 */
export async function fetchTokenWithRetry(
  fetchToken: () => Promise<string | null>,
  {
    isSignedIn,
    delaysMs = TOKEN_RETRY_DELAYS_MS,
    onRetried,
  }: {
    isSignedIn: boolean;
    delaysMs?: readonly number[];
    onRetried?: (result: { attempts: number; recovered: boolean }) => void;
  },
) {
  for (let attempt = 0; ; attempt++) {
    const isLastAttempt = !isSignedIn || attempt === delaysMs.length;
    try {
      const token = await fetchToken();
      if (token || isLastAttempt) {
        if (attempt > 0) onRetried?.({ attempts: attempt + 1, recovered: Boolean(token) });
        return token;
      }
    } catch (error) {
      if (isLastAttempt) {
        if (attempt > 0) onRetried?.({ attempts: attempt + 1, recovered: false });
        throw error;
      }
    }
    await new Promise((resolve) => setTimeout(resolve, delaysMs[attempt]));
  }
}
